const geometric = require("geometric")
import _ from 'lodash'

import { DrawingTool, DrawingToolType, ShapeConfig } from "./drawing-tool";

interface Coordinate {
    x: number
    y: number
}

type GeometricPoint = number[]
type GeometricPolygon = GeometricPoint[]
type GeometricLine = [GeometricPoint, GeometricPoint]

declare const config

const DEFAULT_CIRCLE_RADIUS = config.drawingArea.tools.eraser.defaultCircleRadius
const ANGLE_INCREMENT = config.drawingArea.tools.eraser.angleIncrement

const DEBUG_FILL_COLOR = config.drawingArea.tools.debug.debugFillColor
const DEBUG_STROKE_COLOR = config.drawingArea.tools.debug.debugStrokeColor

export class EraserDrawingTool extends DrawingTool {
    protected TOOL_TYPE: DrawingToolType = DrawingToolType.ERASER

    private eraserRadius
    private previousEraserCoordinate: Coordinate | null = null

    public constructor(strokeWidth: number, color?, eraserRadius: number = DEFAULT_CIRCLE_RADIUS) {
        super(strokeWidth, color)
        this.eraserRadius = eraserRadius
    }

    public onTouchMove(currentShapes: any, currentX: any, currentY: any, startX: any, startY: any, debugShapes?: any): void {
        const currentCoordinate = { x: currentX, y: currentY}
        
        let newShapes: any[] = []
        let shapesIndexToRemove: number[] = []

        const eraserAreaPolygon = this.getAreaToErase(
            currentCoordinate,
            this.eraserRadius,
            ANGLE_INCREMENT
        )

        for(const [index, shape] of currentShapes.entries()) {
            const pointsCoordinates = this.getPointsAsCoordinates(shape.points)

            const additionalShapes = this.updateShape(currentCoordinate, pointsCoordinates, eraserAreaPolygon, shape)
            if(additionalShapes.length) {
                newShapes = newShapes.concat(additionalShapes)
            }
            if(!shape.points?.length) {
                shapesIndexToRemove.push(index)
            }
        }

        if(shapesIndexToRemove.length) {
            for(let i=shapesIndexToRemove.length-1; i>=0; i--) {
                const index = shapesIndexToRemove[i]
                currentShapes.splice(index, 1)
            }
        }

        for(const shape of newShapes) {
            currentShapes.push(shape)
        }

        if(debugShapes) {
            let newDebugShape = _.cloneDeep(debugShapes[debugShapes.length - 1])
            newDebugShape.points = eraserAreaPolygon.flat()
            debugShapes.push(newDebugShape)
        }

        this.previousEraserCoordinate = currentCoordinate
    }

    public onTouchEnd(currentShape: any, currentX: any, currentY: any, startX: any, startY: any): void {
        
    }

    public resetTool() {
        
    }

    protected addNewShape(shapeX: any, shapeY: any, id: any, color?: string | undefined, parameters?: any): ShapeConfig {
        const polygon = this.getAreaToErase(
            {x: shapeX, y: shapeY},
            this.eraserRadius,
            ANGLE_INCREMENT
        )
        
        this.previousEraserCoordinate = null
        return {
            id: this.generateShapeId(id),
            type: this.TOOL_TYPE,
            fill: DEBUG_FILL_COLOR,
            opacity: this.DEFAULT_OPACITY,
            refresh: '', // used to trigger Vue.js watch as pushing in array doesn't do it
            stroke: DEBUG_STROKE_COLOR,
            strokeWidth: this.strokeWidth,
            x: 0,
            y: 0,
            points: polygon.flat(),    
            closed: true,
        } as ShapeConfig
    }

    getPointsAsCoordinates(points: number[]): Coordinate[] {
        let res: Coordinate[] = []
        for(let i=0; i<points.length; i = i+2) {
            res.push({
                x: points[i],
                y: points[i+1]
            })
        }
        return res
    }

    coordinatesToPoints(coordinate: Coordinate[]): number[] {
        return coordinate.flatMap(coordinate => {
            return [coordinate.x, coordinate.y]
        })
    }

    getArea(coordinateStart, coordinateStop) {
        return {
            start: coordinateStart,
            end: coordinateStop,
        }
    }

    coordinateIsInArea(coordinate: Coordinate, area: GeometricPolygon): boolean {
        const point = this.coordinateToGeometricLibPoint(coordinate)
        return geometric.pointInPolygon(point, area)
    }

    lineIntersectsArea(line: [Coordinate, Coordinate], area: GeometricPolygon): boolean {
        const geometricLine: GeometricLine = this.coordinateTupleToGeometricLine(line)
        return geometric.lineIntersectsPolygon(geometricLine, area)
    }

    updateShape(currentCoordinate: Coordinate, pointsCoordinates: Coordinate[], areaToErase: GeometricPolygon, shape) {
        let shapes: any[] = []
        let updatedCoordinates: Coordinate[][] = [[]]
        let updatedCoordinateIndex = 0

        if(pointsCoordinates.length > 1) {
            // handle first point
            const firstSegmentIntersect = this.lineIntersectsArea([pointsCoordinates[0], pointsCoordinates[1]], areaToErase)
            if(firstSegmentIntersect) {
                //this.getSegmentPartOutsideOfArea([pointsCoordinates[0], pointsCoordinates[1]], areaToErase)
            } else {
                const firstPointInArea = this.coordinateIsInArea(pointsCoordinates[0], areaToErase)
                if(!firstPointInArea) {
                    updatedCoordinates[0].push(pointsCoordinates[0])
                }
            }
    
            for(let i=1; i<pointsCoordinates.length-1; i++) {
                const currentPoint = pointsCoordinates[i]
                const previousPoint = pointsCoordinates[i-1]
                const nextPoint = pointsCoordinates[i+1]

                const previousPointIsInsideArea = this.coordinateIsInArea(currentPoint, areaToErase)
                const currentPointIsInsideArea = this.coordinateIsInArea(currentPoint, areaToErase)
                const nextPointIsInsideArea = this.coordinateIsInArea(currentPoint, areaToErase)
                
                const intersectPreviousCurrent = this.lineIntersectsArea([previousPoint, currentPoint], areaToErase)
                const intersectCurrentNext = this.lineIntersectsArea([currentPoint, nextPoint], areaToErase)
                
                if(previousPointIsInsideArea && currentPointIsInsideArea) {
                    updatedCoordinates.push([])
                } else {
                    if(intersectPreviousCurrent) {
                        const lineOutsideOfPolygon = this.getSegmentPartOutsideOfArea([previousPoint, currentPoint], areaToErase)
                        if(lineOutsideOfPolygon) {
                            updatedCoordinates[updatedCoordinateIndex].pop()
                            updatedCoordinates[updatedCoordinateIndex].push(...lineOutsideOfPolygon)
                        }
                        updatedCoordinates.push([])
                        updatedCoordinateIndex++
                    }
                }
                
                if(currentPointIsInsideArea && nextPointIsInsideArea) {
                    updatedCoordinates.push([])
                } else {
                    if(intersectCurrentNext) {
                        updatedCoordinates.push([])
                        updatedCoordinateIndex++
                        const lineOutsideOfPolygon = this.getSegmentPartOutsideOfArea([currentPoint, previousPoint], areaToErase)
                        if(lineOutsideOfPolygon) {
                            updatedCoordinates[updatedCoordinateIndex].push(...lineOutsideOfPolygon)
                        }
                    }
                }
    
                if(!intersectPreviousCurrent && !intersectCurrentNext && !currentPointIsInsideArea) {
                    updatedCoordinates[updatedCoordinateIndex].push(currentPoint)
                }
            }
    
            // handle last point
            const pointBeforeLastPoint = pointsCoordinates[pointsCoordinates.length-2]
            const lastPoint = pointsCoordinates[pointsCoordinates.length-1]
            const lastSegmentIntersect = this.lineIntersectsArea(
                [pointBeforeLastPoint, lastPoint], 
                areaToErase
            )
            if(lastSegmentIntersect) {
                const lineOutsideOfPolygon = this.getSegmentPartOutsideOfArea([pointBeforeLastPoint, lastPoint], areaToErase)
                if(lineOutsideOfPolygon) {
                    updatedCoordinates.push([...lineOutsideOfPolygon])
                }
            } else {
                const lastPointIsInArea = this.coordinateIsInArea(lastPoint, areaToErase)
                if(!lastPointIsInArea) {
                    updatedCoordinates[updatedCoordinateIndex].push(lastPoint)
                }
            }    
        }

        shape.points = this.coordinatesToPoints(updatedCoordinates[0])
        shape.refresh = `${currentCoordinate.x},${currentCoordinate.y}`
        
        if(updatedCoordinates.length > 1) {
            for(const newShapeCoordinates of updatedCoordinates.slice(1)) {
                if(newShapeCoordinates.length) {
                    const newShape = _.cloneDeep(shape)
                    newShape.points = this.coordinatesToPoints(newShapeCoordinates)
                    if(!newShape.points.length) {
                        console.debug('New shape points error')
                    }
                    shapes.push(newShape)
                }
            }
        }

        return shapes
    }

    private coordinateToGeometricLibPoint(coordinate: Coordinate): GeometricPoint {
        return [coordinate.x, coordinate.y]
    }

    private coordinateTupleToGeometricLine(coordinateTuple: [Coordinate, Coordinate]): GeometricLine {
        return coordinateTuple.map(coordinate => {
            return this.coordinateToGeometricLibPoint(coordinate)
        }) as GeometricLine
    }

    /**
     * 
     * @param line 
     * @param distance 
     * @param direction 
     * @returns 
     */
    private getTranslatedSegment(line: GeometricLine, distance: number, direction: "p1ToP2" | "p2ToP1"): GeometricLine {
        let distanceAndOrientation = distance
        if(line[0][0] > line[1][0]) {
            if(direction === "p1ToP2") {
                distanceAndOrientation = -distance
            }
        } 
        else if(line[1][0] > line[0][0]) {
            if(direction === "p2ToP1") {
                distanceAndOrientation = -distance
            }
        }
        else {
            // the x are equals for the 2 points
            if(line[0][1] > line[1][1]) {
                if(direction == "p1ToP2") {
                    distanceAndOrientation = -distance
                }
            }
            else if(line[1][1] > line[0][1]) {
                if(direction == "p2ToP1") {
                    distanceAndOrientation = -distance
                }
            }
        }
        
        return geometric.lineTranslate(line, 0, distanceAndOrientation)
    }

    private getAreaToErase(currentCoordinate: Coordinate, radius: number, angleIncrement: number): GeometricPolygon {
        if(!this.previousEraserCoordinate) {
            return this.getCirclePolygon(
                currentCoordinate,
                radius,
                angleIncrement
            )
        }

        const previousToCurrentLine: GeometricLine = [
            this.coordinateToGeometricLibPoint(this.previousEraserCoordinate),
            this.coordinateToGeometricLibPoint(currentCoordinate)
        ]

        const segmentThatStartAtRadiusFromP1 = this.getTranslatedSegment(
            previousToCurrentLine,
            radius,
            "p1ToP2"
        )

        const segmentThatStartAtRadiusFromP2 = this.getTranslatedSegment(
            previousToCurrentLine,
            radius,
            "p2ToP1"
        )

        let areaPolygon: GeometricPolygon = []

        for(let i=90; i<=270; i+=angleIncrement) {
            areaPolygon.push(
                geometric.pointRotate(
                    previousToCurrentLine[0], 
                    i, 
                    segmentThatStartAtRadiusFromP1[0],
                )
            )
        }

        for(let i=90; i<=270; i+=angleIncrement) {
            areaPolygon.push(
                geometric.pointRotate(
                    previousToCurrentLine[1], 
                    i, 
                    segmentThatStartAtRadiusFromP2[1]
                )
            )
        }

        return areaPolygon 
    }

    /**
     * 
     * @param centerPoint 
     * @param radius 
     * @param angleIncrement angle in degre
     */
    private getCirclePolygon(centerPoint: Coordinate, radius: number, angleIncrement: number): GeometricPolygon {
        const circleCenterPoint = this.coordinateToGeometricLibPoint(centerPoint)
        const pointOnCircleBorder = this.coordinateToGeometricLibPoint({
            x: centerPoint.x + radius,
            y: centerPoint.y
        })

        let circleBorderPoints: GeometricPolygon = []
        for(let i=0; i<=360 - angleIncrement; i+=angleIncrement) {
            circleBorderPoints.push(
                geometric.pointRotate(
                    circleCenterPoint, 
                    i, 
                    pointOnCircleBorder
                )
            )
        }

        const circlePolygon = [
            ...circleBorderPoints,
            geometric.pointRotate(circleCenterPoint, 360, pointOnCircleBorder),
        ]

        return circlePolygon
    }

    /**
     * If the segment does not cross the area, the original segment is returned.
     * If the segment is entirely inside the area null is returned 
     * @param segment 
     * @param area 
     * @returns The segment provided without the part that is inside the area. The order of the points given in 
     * the segment param is preserved
     */
    private getSegmentPartOutsideOfArea(segment: [Coordinate, Coordinate], area: GeometricPolygon): [Coordinate, Coordinate] | null {
        const point1InArea = this.coordinateIsInArea(segment[0], area)
        const point2InArea = this.coordinateIsInArea(segment[1], area)
        if(point1InArea && point2InArea) {
            return null
        }

        for(let i=1; i<area.length; i++) {
            const intersection = this.getIntersectionPointBetween2Lines(
                area[i][0], area[i][0][1], 
                area[i-1][0], area[i-1][1],
                segment[0][0], segment[0][1],
                segment[1][0], segment[1][1]
            )

            if(intersection) {
                if(point1InArea) {
                    return [
                        intersection,
                        segment[1]
                    ]
                }
                return [
                    segment[0],
                    intersection
                ]
            }
        }

        return segment
    }

    /**
     * Determine the intersection point of two line segments
     * line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
     * @param x1 
     * @param y1 
     * @param x2 
     * @param y2 
     * @param x3 
     * @param y3 
     * @param x4 
     * @param y4 
     * @returns The coordinates of the point or FALSE if the lines don't intersect
     */
    private getIntersectionPointBetween2Lines(x1, y1, x2, y2, x3, y3, x4, y4): Coordinate | false {

        // Check if none of the lines are of length 0
        if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
            return false
        }
    
        const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1))
    
        // Lines are parallel
        if (denominator === 0) {
            return false
        }
    
        let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
        let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator
    
        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return false
        }
    
        // Return a object with the x and y coordinates of the intersection
        let x = x1 + ua * (x2 - x1)
        let y = y1 + ua * (y2 - y1)
    
        return {x, y}
    }
}